<template>
  <div class="bg-white rounded-lg border border-gray-200 p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h3 class="text-lg font-medium text-gray-900">
          {{ $t('BUSINESS_DATA.FAQ.TITLE') }}
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t('BUSINESS_DATA.FAQ.DESCRIPTION') }}
        </p>
      </div>
      <woot-button @click="$emit('add-faq')">
        <fluent-icon icon="add" size="16" class="mr-2" />
        {{ $t('BUSINESS_DATA.FAQ.ADD_NEW') }}
      </woot-button>
    </div>
    
    <!-- Pagination Table -->
    <PaginationTable
      :items="faqs"
      :categories="categories"
      :pagination="pagination"
      :is-loading="isLoading"
      :is-searching="isSearching"
      empty-icon="chat-help"
      empty-title-key="BUSINESS_DATA.FAQ.EMPTY.TITLE"
      empty-description-key="BUSINESS_DATA.FAQ.EMPTY.DESCRIPTION"
      add-button-key="BUSINESS_DATA.FAQ.ADD_NEW"
      @search="$emit('search', $event)"
      @filter-change="$emit('filter-change', $event)"
      @page-change="$emit('page-change', $event)"
      @add-item="$emit('add-faq')"
      @edit-item="$emit('edit-faq', $event)"
      @toggle-status="toggleFaqStatus"
      @delete-item="deleteFaq"
    >
      <template #default="{ items, onEdit, onToggleStatus, onDelete }">
        <div class="space-y-4">
          <div 
            v-for="faq in items" 
            :key="faq.id" 
            class="faq-item border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <!-- Question -->
                <div class="flex items-center gap-2 mb-2">
                  <fluent-icon icon="chat-help" size="16" class="text-blue-500" />
                  <h4 class="font-medium text-gray-900">{{ faq.question }}</h4>
                  <span 
                    v-if="faq.category"
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {{ faq.category }}
                  </span>
                </div>
                
                <!-- Answer -->
                <p class="text-gray-600 text-sm mb-3">{{ faq.answer }}</p>
                
                <!-- Attached Images -->
                <div v-if="faq.has_images && faq.attached_images?.length" class="mb-3">
                  <div class="flex flex-wrap gap-2">
                    <div
                      v-for="img in faq.attached_images"
                      :key="img.id"
                      class="relative group"
                    >
                      <div class="w-12 h-12 rounded-lg overflow-hidden border border-gray-200">
                        <img :src="img.thumb_url || img.file_url" class="w-full h-full object-cover" />
                      </div>
                      <div class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {{ img.title }}
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Meta info -->
                <div class="flex items-center gap-4 text-xs text-gray-500">
                  <span v-if="faq.business_category" class="inline-flex items-center">
                    <span
                      class="px-2 py-1 rounded-full"
                      :style="{
                        backgroundColor: faq.business_category.color,
                        color: getTextColor(faq.business_category.color)
                      }"
                    >
                      {{ faq.business_category.name }}
                    </span>
                  </span>
                  <span class="font-mono bg-gray-100 px-2 py-1 rounded">ID: {{ faq.id }}</span>
                  <span>{{ $t('BUSINESS_DATA.FAQ.META.CREATED') }}: {{ formatDate(faq.created_at) }}</span>
                  <span 
                    :class="faq.is_active ? 'text-green-600' : 'text-red-600'"
                  >
                    {{ faq.is_active ? $t('BUSINESS_DATA.FAQ.META.ACTIVE') : $t('BUSINESS_DATA.FAQ.META.INACTIVE') }}
                  </span>
                </div>
              </div>
              
              <!-- Actions -->
              <div class="flex items-center gap-2 ml-4">
                <woot-button
                  variant="clear"
                  size="small"
                  @click="onEdit(faq)"
                >
                  <fluent-icon icon="edit" size="14" />
                </woot-button>
                
                <woot-button
                  variant="clear"
                  size="small"
                  color-scheme="secondary"
                  @click="onToggleStatus(faq)"
                >
                  <fluent-icon 
                    :icon="faq.is_active ? 'eye-hide' : 'eye-show'" 
                    size="14" 
                  />
                </woot-button>
                
                <woot-button
                  variant="clear"
                  size="small"
                  color-scheme="danger"
                  @click="onDelete(faq)"
                >
                  <fluent-icon icon="delete" size="14" />
                </woot-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </PaginationTable>
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import PaginationTable from './PaginationTable.vue';
function getTextColor(backgroundColor) {
  if (!backgroundColor) return '#000000';
  const hex = String(backgroundColor).replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export default {
  name: 'FaqSection',
  
  components: {
    PaginationTable
  },
  
  props: {
    faqs: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['refresh', 'add-faq', 'edit-faq', 'search', 'filter-change', 'page-change'], 
  
  computed: {
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    }
  },
  
  methods: {
    getTextColor,
    async toggleFaqStatus(faq) {
      try {
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/business_faqs/${faq.id}`, {
          is_active: !faq.is_active
        });
        this.$emit('refresh');
        useAlert(
          faq.is_active 
            ? this.$t('BUSINESS_DATA.FAQ.DEACTIVATED_SUCCESS')
            : this.$t('BUSINESS_DATA.FAQ.ACTIVATED_SUCCESS'),
          'success'
        );
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED'), 'error');
      }
    },
    
    async deleteFaq(faq) {
      if (!window.confirm(this.$t('BUSINESS_DATA.FAQ.DELETE_CONFIRM'))) {
        return;
      }
      
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/business_faqs/${faq.id}`);
        this.$emit('refresh');
        useAlert(this.$t('BUSINESS_DATA.FAQ.DELETED_SUCCESS'), 'success');
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.ERROR.DELETE_FAILED'), 'error');
      }
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    }
  },
};
</script>
